import { useEffect, useSyncExternalStore } from "react";
import { useAuth } from "@/auth/useAuth";
import { platformErrorsApi } from "@/features/observability/platform-errors/api/platform-errors.api";

// UX only: the backend decides every read (role + revocation). Hiding the entry is not the control;
// the page reached by URL still gets the backend's opaque 403.
const featureEnabled = (): boolean => process.env.REACT_APP_PLATFORM_ERRORS_ENABLED === "true";

export type PlatformErrorsAccessStatus = "idle" | "checking" | "allowed" | "forbidden" | "unavailable";

export type PlatformErrorsAccessState = { userId: string; status: PlatformErrorsAccessStatus };

const INITIAL: PlatformErrorsAccessState = { userId: "", status: "idle" };

let state: PlatformErrorsAccessState = INITIAL;
const listeners = new Set<() => void>();

const publish = (next: PlatformErrorsAccessState): void => {
    state = next;
    listeners.forEach((listener) => listener());
};

const getState = (): PlatformErrorsAccessState => state;

const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

const reset = (): void => publish(INITIAL);

// One probe per user and session (the sidebar remounts on every page); only an unavailable answer is re-asked.
const load = async (userId: string): Promise<void> => {
    if (state.userId === userId && state.status !== "idle" && state.status !== "unavailable") return;
    publish({ userId, status: "checking" });
    const answer = await platformErrorsApi.access(new AbortController().signal);
    if (state.userId !== userId) return;
    publish({ userId, status: answer });
};

const store = { getState, subscribe, load, reset };

const useAccess = (): PlatformErrorsAccessStatus => {
    const { state: auth } = useAuth();
    const snapshot = useSyncExternalStore(subscribe, getState);
    const userId = auth.user === null ? "" : auth.user.id;
    const authenticated = auth.status === "authenticated";

    useEffect(() => {
        if (featureEnabled() === false || authenticated === false || userId === "") return;
        void load(userId);
    }, [authenticated, userId]);

    if (featureEnabled() === false || snapshot.userId !== userId) return "idle";
    return snapshot.status;
};

export const platformErrorsAccess = { featureEnabled, store, useAccess };
